import React, { useEffect } from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import {
  BadgeOutlined,
  CategoryOutlined,
  PeopleOutline,
  InfoOutlined,
  LocalShippingOutlined,
  Inventory2Outlined,
  SendOutlined,
} from "@mui/icons-material";

import { useDriverDetailContext } from "contexts/DriverDetailContext";

import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import Status from "components/Shared/Status";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { useLazyGetDriverShipmentCountsQuery } from "store/services/driverApi";

const DriverBasicDetail = () => {
  const { driverDetail, loadingDriver } = useDriverDetailContext();

  const [fetchCounts, { data: countsRes, isLoading, isFetching }] =
    useLazyGetDriverShipmentCountsQuery();

  useEffect(() => {
    if (driverDetail?._id) {
      fetchCounts(driverDetail._id);
    }
  }, [driverDetail?._id, fetchCounts]);

  const loadingCounts = isLoading || isFetching;

  return !loadingDriver ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={4}>
          <InformationTile
            icon={() => <BadgeOutlined color="primary" />}
            title="Driver ID"
            subtitle={driverDetail?.driverId}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <PeopleOutline color="primary" />}
            title="Name"
            subtitle={driverDetail?.name}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <PeopleOutline color="primary" />}
            title="Connected User"
            subtitle={
              driverDetail?.driverUser && typeof driverDetail.driverUser === "object" ? (
                <OpenInNewTab
                  label={driverDetail.driverUser.name || "N/A"}
                  link={`/app/crm/user/${driverDetail.driverUser._id}/detail`}
                />
              ) : (
                "N/A"
              )
            }
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <CategoryOutlined color="primary" />}
            title="Email"
            subtitle={driverDetail?.email}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <CategoryOutlined color="primary" />}
            title="Phone"
            subtitle={driverDetail?.phone}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <CategoryOutlined color="primary" />}
            title="Assigned Vehicles"
            subtitle={driverDetail?.assigned_vehicle_ids
              ?.map((el) => el?.registration_number)
              ?.join(", ")}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <CategoryOutlined color="primary" />}
            title="Medical Certification"
            subtitle={driverDetail?.medicalCertificationStatus?.name}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="License ID"
            subtitle={(() => {
              const licences = Array.isArray(driverDetail?.driverLicence)
                ? driverDetail.driverLicence
                : driverDetail?.driverLicence
                  ? [driverDetail.driverLicence]
                  : [];

              return (
                licences
                  .filter((licence) => licence?.isActive)
                  .map((licence) => licence?.licenceIdd)
                  .filter(Boolean)
                  .join(", ") || "N/A"
              );
            })()}
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile icon={() => <InfoOutlined color="primary" />} title="Status">
            <Status isActive={driverDetail?.isActive} />
          </InformationTile>
        </Grid>

        {/* Dynamic Count Tiles */}
        <Grid item xs={4}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Collected Shipments"
            subtitle={
              loadingCounts ? (
                <CircularProgress size={20} />
              ) : (
                <Typography variant="h6" color="primary" fontWeight={900}>
                  {countsRes?.collectedCount ?? 0}
                </Typography>
              )
            }
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <Inventory2Outlined color="primary" />}
            title="Loaded Shipments"
            subtitle={
              loadingCounts ? (
                <CircularProgress size={20} />
              ) : (
                <Typography variant="h6" color="secondary" fontWeight={900}>
                  {countsRes?.loadedCount ?? 0}
                </Typography>
              )
            }
          />
        </Grid>

        <Grid item xs={4}>
          <InformationTile
            icon={() => <SendOutlined color="primary" />}
            title="Send Out Shipments"
            subtitle={
              loadingCounts ? (
                <CircularProgress size={20} />
              ) : (
                <Typography variant="h6" color="success.main" fontWeight={900}>
                  {countsRes?.sendOutCount ?? 0}
                </Typography>
              )
            }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default DriverBasicDetail;